import { SentMessageId } from "@sp-crm/core";
import React from "react";
import { isoDateStringToHumanReadable } from "util/date/locale-date-format";
import { formatContactDisplay, UnifiedMessage } from "./unified-message";

interface ReceivedMessageExpandedProps {
    message: UnifiedMessage;
}

const ReceivedMessageExpanded: React.FC<ReceivedMessageExpandedProps> = ({ message }) => {
    const original = message.originalReceived;
    if (!original) return null;

    return (
        <div className="space-y-1 text-sm">
            <div>
                <span className="font-medium text-gray-700">From: </span>
                <span className="text-gray-900">{message.fromDisplay}</span>
            </div>
            <div>
                <span className="font-medium text-gray-700">To: </span>
                <span className="text-gray-900">
                    {original.to
                        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
                        .join(", ")}
                </span>
            </div>
            {original.cc.length > 0 ? (
                <div>
                    <span className="font-medium text-gray-700">CC: </span>
                    <span className="text-gray-900">
                        {original.cc
                            .map(r =>
                                formatContactDisplay(r.contact.name, r.contact.email),
                            )
                            .join(", ")}
                    </span>
                </div>
            ) : null}
            {original.bcc.length > 0 ? (
                <div>
                    <span className="font-medium text-gray-700">BCC: </span>
                    <span className="text-gray-900">
                        {original.bcc
                            .map(r =>
                                formatContactDisplay(r.contact.name, r.contact.email),
                            )
                            .join(", ")}
                    </span>
                </div>
            ) : null}
            <div>
                <span className="font-medium text-gray-700">Received: </span>
                <span className="text-gray-900">
                    {isoDateStringToHumanReadable(original.receivedTimestamp)}
                </span>
            </div>
            {original.bodyPreview ? (
                <div className="mt-3 text-gray-700">{original.bodyPreview}</div>
            ) : null}
        </div>
    );
};

interface SentMessageExpandedProps {
    message: UnifiedMessage;
    onViewFull: (id: SentMessageId) => void;
}

const SentMessageExpanded: React.FC<SentMessageExpandedProps> = ({
    message,
    onViewFull,
}) => {
    const original = message.originalSent;
    if (!original) return null;

    return (
        <div className="space-y-1 text-sm">
            <div>
                <span className="font-medium text-gray-700">To: </span>
                <span className="text-gray-900">{(original.to ?? []).join(", ")}</span>
            </div>
            <div>
                <span className="font-medium text-gray-700">Sent: </span>
                <span className="text-gray-900">
                    {isoDateStringToHumanReadable(original.createdAt)}
                </span>
            </div>
            {original.attachments && original.attachments.length > 0 ? (
                <div>
                    <span className="font-medium text-gray-700">Attachments: </span>
                    <span className="text-gray-900">
                        {original.attachments.join(", ")}
                    </span>
                </div>
            ) : null}
            <div className="pt-2">
                <button
                    type="button"
                    onClick={e => {
                        e.stopPropagation();
                        onViewFull(original.id);
                    }}
                    className="text-brand-600 hover:text-brand-800 font-medium text-sm">
                    View full message
                </button>
            </div>
        </div>
    );
};

interface SmsThreadExpandedProps {
    message: UnifiedMessage;
}

const SmsThreadExpanded: React.FC<SmsThreadExpandedProps> = ({ message }) => {
    const smsThread = message.originalSmsThread;
    if (!smsThread) return null;

    return (
        <div className="space-y-3 text-sm">
            <div>
                <span className="font-medium text-gray-700">Phone: </span>
                <span className="text-gray-900">{smsThread.contact.phoneNumber}</span>
            </div>
            <div className="space-y-2">
                {smsThread.messages.slice(0, 5).map(msg => (
                    <div
                        key={msg.id}
                        className={`p-2 rounded text-sm ${
                            msg.direction === "Outbound"
                                ? "bg-brand-50 ml-4"
                                : "bg-gray-100 mr-4"
                        }`}>
                        <div className="text-gray-700">{msg.body}</div>
                        <div className="text-xs text-gray-400 mt-1">
                            {isoDateStringToHumanReadable(msg.dateAdded)}
                        </div>
                    </div>
                ))}
                {smsThread.messages.length > 5 ? (
                    <div className="text-gray-500 text-sm">
                        ... and {smsThread.messages.length - 5} more messages
                    </div>
                ) : null}
            </div>
        </div>
    );
};

interface MessageRowExpandedProps {
    message: UnifiedMessage;
    onViewFullSentMessage: (id: SentMessageId) => void;
}

export const MessageRowExpanded: React.FC<MessageRowExpandedProps> = ({
    message,
    onViewFullSentMessage,
}) => {
    if (message.type === "received") {
        return <ReceivedMessageExpanded message={message} />;
    }

    if (message.type === "sent") {
        return (
            <SentMessageExpanded message={message} onViewFull={onViewFullSentMessage} />
        );
    }

    if (message.type === "sms") {
        return <SmsThreadExpanded message={message} />;
    }

    return null;
};
